import { useEffect } from 'react';

import {
	type ApiTask,
	type ApiTaskSSEEventType,
	type ApiTaskSSEUpdate,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import {
	subscribeToTasksSSE,
	type TasksSSEListener,
} from '@atlassian/conversation-assistant-service/services/tasks-sse/tasks-sse-connection';
import { useTasksStoreActions } from '@atlassian/conversation-assistant-store/controllers/tasks';

export type FetchTasksResult = {
	/** Whether the fetch has settled (resolved or rejected). */
	loaded: boolean;
	tasks: ApiTask[];
	error: Error | null;
};

type HookWithAssistanceServiceParams<P> = Omit<P, 'assistanceServiceInjectedParams'> &
	AssistanceServiceInputParam;

const useFetchTasksWithAssistanceService = ({
	assistanceServiceInjectedParams: { assistanceService },
}: AssistanceServiceInjectedParams): FetchTasksResult => {
	const { data, error, refetch } = useQuery({
		queryKey: ['getTasks'],
		queryFn: () => assistanceService.getTasks(),
		enabled: false,
	});

	// Fetch on every mount
	useEffect(() => {
		refetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		tasks: data?.tasks ?? [],
		// Skeleton only on cold load (no cached data yet)
		loaded: data !== null,
		error: error ? new Error(error.message) : null,
	};
};

/** Hook that fetches the current user's task list from GET /rovo/v1/tasks on mount. */
export const useFetchTasks: (
	props: HookWithAssistanceServiceParams<AssistanceServiceInjectedParams>,
) => FetchTasksResult = createHookWithAssistanceService(useFetchTasksWithAssistanceService);

/**
 * Module-level tracker for the last event ingested into the tasks store.
 * Since `subscribeToTasksSSE` shares a single connection and fans out the
 * same event object reference to every subscriber, we use reference equality
 * to ensure `ingestTasksSSEEvent` is called only once per event, regardless
 * of how many hook instances are mounted.
 */
let lastIngestedEvent: { event: ApiTaskSSEUpdate; eventType: ApiTaskSSEEventType } | null = null;

/**
 * Initialises the Tasks SSE connection for UI that needs live task lifecycle updates.
 *
 * Prefer mounting this only where consumption happens (e.g. conversation history list refresh,
 * side navigation when open) plus `enabled: fg('rovo_chat_enable_tasks')`, so idle assistant
 * sessions do not open `/rovo/v1/tasks/stream`. Multiple mounts are OK: the connection manager
 * is **ref-counted** (shared connection while any subscriber is active).
 *
 * SSE events are written to the **tasks** store (`@atlassian/conversation-assistant-store/controllers/tasks`).
 * Consumers read updates via `useTasksUpdates()` without owning the transport.
 *
 * Store ingestion (`ingestTasksSSEEvent`) is deduplicated at the module level so that only
 * one subscriber per event actually writes to the store, even when multiple hook instances
 * are mounted simultaneously.
 *
 * @param options.enabled - When false, no connection is established. Defaults to true.
 * @param options.onEvent - Optional additional listener called for every SSE event.
 */
const useInitTasksSSEWithAssistanceService = ({
	enabled = true,
	assistanceServiceInjectedParams: { assistanceService },
	onEvent,
}: {
	enabled?: boolean;
	/**
	 * Optional additional listener invoked for every SSE event alongside the
	 * default store ingestion. Useful for consumers that want to react to
	 * specific event types (e.g. `task_status`) directly.
	 */
	onEvent?: TasksSSEListener;
} & AssistanceServiceInjectedParams): void => {
	const { ingestTasksSSEEvent } = useTasksStoreActions();

	useEffect(() => {
		if (!enabled) {
			return;
		}

		return subscribeToTasksSSE(
			() => assistanceService,
			(event: ApiTaskSSEUpdate, eventType: ApiTaskSSEEventType) => {
				// Deduplicate store ingestion: the shared connection fans the same
				// event object reference to every subscriber, so we only ingest
				// when the reference differs from the last one we processed.
				if (lastIngestedEvent?.event !== event) {
					ingestTasksSSEEvent(event, eventType);
					lastIngestedEvent = { event, eventType };
				}

				// Always forward to the custom handler if provided.
				onEvent?.(event, eventType);
			},
		);
	}, [assistanceService, enabled, ingestTasksSSEEvent, onEvent]);
};

export const useInitTasksSSE: (
	props: HookWithAssistanceServiceParams<
		{
			enabled?: boolean;
			onEvent?: TasksSSEListener;
		} & AssistanceServiceInjectedParams
	>,
) => void = createHookWithAssistanceService(useInitTasksSSEWithAssistanceService);
